import { useState, useEffect, useRef } from 'react';
import type { Skill } from '../../models/v2/profile';
import GlassCard from '../ui/v2/GlassCard';
import { RiHeartFill, RiPencilLine, RiSearchLine, RiHeartLine, RiCloseLine } from 'react-icons/ri';
import ProfileSidePanel from './ProfileSidePanel';
import { profileService } from '../../services/profileService';
import { useProfileMutation, useProfile } from '../../context/ProfileContext';

interface Props {
    skills: Skill[];
    onUpdate?: () => void;
}

export default function SkillsSection({ skills, onUpdate }: Props) {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [localSkills, setLocalSkills] = useState<Skill[]>([]);
    const [availableSkills, setAvailableSkills] = useState<Skill[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { mutate, isMutating } = useProfileMutation();
    const { updateLocalState } = useProfile();

    // Fetch available skills when panel opens
    useEffect(() => {
        if (isPanelOpen) {
            setLocalSkills([...skills]); // Initialize with current skills
            loadAvailableSkills();
        }
    }, [isPanelOpen]);

    // Click outside to close dropdown
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const loadAvailableSkills = async () => {
        try {
            const allSkills = await profileService.getAvailableSkills();
            setAvailableSkills(allSkills);
        } catch (error) {
            console.error("Failed to load skills", error);
        }
    };

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        setIsDropdownOpen(true);
    };

    const addSkill = (skill: Skill) => {
        if (!localSkills.find(s => s.name === skill.name)) {
            setLocalSkills([...localSkills, { ...skill, is_highlighted: false }]);
        }
        setSearchQuery('');
        setIsDropdownOpen(false);
    };

    const removeSkill = (skillId: string) => {
        setLocalSkills(localSkills.filter(s => s.id !== skillId));
    };

    const toggleHighlight = (skillId: string) => {
        setLocalSkills(localSkills.map(s =>
            s.id === skillId ? { ...s, is_highlighted: !s.is_highlighted } : s
        ));
    };

    const handleSave = () => {
        mutate(localSkills, {
            mutationFn: (data: any) => profileService.updateSkills(data),
            onSuccess: () => {
                // Optimistic update
                updateLocalState(prev => {
                    if (!prev) return prev;
                    return { ...prev, skills: localSkills };
                });

                setIsPanelOpen(false);
                if (onUpdate) onUpdate();
            },
            successMessage: 'Skills updated successfully'
        });
    };

    // Filter available skills that are NOT already selected
    const filteredAvailable = availableSkills
        .filter(s =>
            !localSkills.find(ls => ls.name === s.name) &&
            s.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

    return (
        <GlassCard>
            <div className="flex items-center justify-between mb-4">
                <div className="space-y-1">
                    <h2 className="text-lg font-bold text-white">Skills</h2>
                    <p className="text-xs text-gray-500">Skills that you'd prefer to utilize in roles are highlighted with a heart 💙</p>
                </div>
                <button
                    onClick={() => setIsPanelOpen(true)}
                    className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
                >
                    <RiPencilLine size={20} />
                </button>
            </div>

            {skills.length === 0 ? (
                <p className="text-gray-500 text-sm">No skills listed.</p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {skills.map((skill) => (
                        <span
                            key={skill.id}
                            className={`
                                flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border
                                ${skill.is_highlighted
                                    ? 'bg-blue-500/10 text-blue-200 border-blue-500/20'
                                    : 'bg-white/5 text-gray-400 border-white/5'
                                }
                            `}
                        >
                            {skill.is_highlighted && <RiHeartFill className="text-blue-500 text-[10px]" />}
                            {skill.name}
                        </span>
                    ))}
                </div>
            )}

            <ProfileSidePanel
                isOpen={isPanelOpen}
                onClose={() => setIsPanelOpen(false)}
                title="Edit Skills"
                onSave={handleSave}
                isSaving={isMutating}
            >
                <div className="space-y-6">
                    <p className="text-sm text-gray-400">
                        Skills that you'd prefer to utilize in roles are highlighted with a heart <span className="text-blue-400">💙</span>
                    </p>

                    {/* Search Input */}
                    <div className="relative" ref={dropdownRef}>
                        <div className="relative">
                            <input
                                type="text"
                                placeholder="Search all skills..."
                                className="w-full px-10 py-3 rounded-xl bg-black/20 border border-white/10 focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 text-white placeholder-gray-600 outline-none transition-all"
                                value={searchQuery}
                                onChange={handleSearch}
                                onFocus={() => setIsDropdownOpen(true)}
                            />
                            <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                        </div>

                        {/* Dropdown Results */}
                        {isDropdownOpen && searchQuery && (
                            <div className="absolute top-full left-0 right-0 mt-2 max-h-60 overflow-y-auto bg-[#1a1d24] border border-white/10 rounded-xl shadow-2xl z-50 custom-scrollbar">
                                {filteredAvailable.length > 0 ? (
                                    filteredAvailable.map(skill => (
                                        <button
                                            key={skill.id}
                                            onClick={() => addSkill(skill)}
                                            className="w-full text-left px-4 py-3 hover:bg-white/5 text-gray-300 hover:text-white transition-colors border-b border-white/5 last:border-0"
                                        >
                                            {skill.name}
                                        </button>
                                    ))
                                ) : (
                                    <div className="px-4 py-3 text-gray-500 text-sm">No matching skills found</div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Selected Skills Chips */}
                    <div className="flex flex-wrap gap-2">
                        {localSkills.map((skill) => (
                            <div
                                key={skill.id}
                                className={`
                                    flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full text-sm font-medium transition-all border group select-none
                                    ${skill.is_highlighted
                                        ? 'bg-white text-black border-white'
                                        : 'bg-white/5 text-gray-300 border-white/10 hover:border-white/30'
                                    }
                                `}
                            >
                                <button
                                    onClick={() => toggleHighlight(skill.id)}
                                    className="hover:scale-110 transition-transform"
                                >
                                    {skill.is_highlighted ? (
                                        <RiHeartFill className="text-blue-500" size={16} />
                                    ) : (
                                        <RiHeartLine className="text-gray-500 group-hover:text-gray-400" size={16} />
                                    )}
                                </button>

                                <span>{skill.name}</span>

                                <button
                                    onClick={() => removeSkill(skill.id)}
                                    className={`
                                        p-0.5 rounded-full transition-colors ml-1
                                        ${skill.is_highlighted
                                            ? 'text-gray-400 hover:text-gray-600 hover:bg-black/10'
                                            : 'text-gray-500 hover:text-white hover:bg-white/10'
                                        }
                                    `}
                                >
                                    <RiCloseLine size={16} />
                                </button>
                            </div>
                        ))}

                        {localSkills.length === 0 && (
                            <p className="text-gray-500 text-sm italic w-full text-center py-4">
                                No skills selected. Search above to add some!
                            </p>
                        )}
                    </div>
                </div>
            </ProfileSidePanel>
        </GlassCard>
    );
}
